import { DataGridPremium, GridCallbackDetails, GridColDef, GridEventListener, GridRenderCellParams, GridRenderEditCellParams, GridRowEditStopReasons, GridRowModel, GridRowModes, GridRowModesModel, GridRowParams, useGridApiContext, useGridApiRef } from '@mui/x-data-grid-premium';
import { Status, useFetch } from '../../hooks/useFetch';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { Airline, Airport } from '@aviation/cabinapp-airlinedata-sdk';
import { Autocomplete, Chip, CircularProgress, IconButton, SvgIcon, TextField } from '@mui/material';
import { ReactComponent as CrossIcon } from '../../icons/cross.svg';
import { ReactComponent as CheckmarkIcon } from '../../icons/checkmark.svg';
import { DynamoDbObject } from '@aviation/cabinapp-common';
import { toast } from 'react-toastify';
import { put } from 'aws-amplify/api';

export interface IAirportsGridProps {
    filterTerm? : string;
}

export interface IAirportsGridRef {
  clearFilter: () => void;
}

const RecyclingAirlinesEditCell = (params: GridRenderEditCellParams & { options: Array<Airline> }) => {
    const apiRef = useGridApiContext();
    const { id, field, value, options } = params;
    const selected = options.filter(o => (value as Array<string> ?? []).includes(o.Code ?? ''));

    return (
        <Autocomplete
            multiple
            fullWidth
            disableCloseOnSelect
            size='small'
            options={options}
            value={selected}
            getOptionLabel={(o) => o.Code ?? ''}
            isOptionEqualToValue={(o, v) => o.Code === v.Code}
            onChange={(_, newValue) => {
                apiRef.current.setEditCellValue({ id, field, value: newValue.map(o => o.Code) });
            }}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip label={option.Code} size='small' {...getTagProps({ index })} />
                ))
            }
            renderInput={(inputParams) => <TextField {...inputParams} variant='standard' />}
            sx={{ width: '100%', px: 1 }}
        />
    );
};

const AirportsGrid = forwardRef<IAirportsGridRef, IAirportsGridProps>((props, ref) => {
    const apiRef = useGridApiRef();
    const { status, data = [] } = useFetch<Array<Airport>>(`/api/airline/airport`, (response) => response);
    const { data: airlines = [] } = useFetch<Array<Airline>>(`/api/airline/airline`, (response) => response);
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [loading] = useState(false);

    const clearFilter = () => {
        apiRef.current.setFilterModel({ items: [] });
    };

    useImperativeHandle(ref, () => ({
        clearFilter,
    }));

    const rowId = (row : any) : string => {
        return `${row.Code}#${row.CityCode}`;
    }

    const filterByTerm = (term : string | undefined, item : Airport) : Boolean => {
        if(term === undefined || term === '')
            return true;

        const code = (item.Code)?.toUpperCase().includes(term) ?? false;
        const name = (item.Name)?.toUpperCase().includes(term) ?? false;
        const country = (item.CountryCode)?.toUpperCase().includes(term) ?? false;
        const city = (item.City)?.toUpperCase().includes(term) ?? false;
       
        return code || name || country || city;
    };

    const columns: GridColDef[] = [
        { field: 'Code', headerName: 'Code', minWidth: 100, editable: false },
        { field: 'Name', headerName: 'Name', minWidth: 200, editable: false},
        { field: 'City', headerName: 'Location', minWidth: 200, editable: false },
        { field: 'SupportsGoNow', headerName: 'GoNow', minWidth: 100, type: 'boolean', editable: true },
        { field: 'SupportsRecyclingAirlines', headerName: 'Recycling Airlines', minWidth: 250, editable: true, sortable: false, filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                const codes : Array<string> = params.value ?? [];
                return codes.join(', ');
            },
            renderEditCell: (params: GridRenderEditCellParams) => <RecyclingAirlinesEditCell {...params} options={airlines} />
        },
        { field: 'SupportsDisinsection', headerName: 'Disinsection', minWidth: 100,  type: 'boolean', editable: true },
        { field: 'actions', type: 'actions', headerName: '', minWidth: 140, renderCell: ({ row }: Partial<GridRowParams>) => {
            const isInEditMode = (rowModesModel[rowId(row)]?.mode ?? GridRowModes.View) === GridRowModes.Edit;
            const isLoading = row.IsLoading ?? false;

            if(isInEditMode) 
                return [
                    <IconButton color="success" onClick={handleSaveClick(row)}>
                        <SvgIcon component={CheckmarkIcon} inheritViewBox />
                    </IconButton>,
                    <IconButton color="error" onClick={handleCancelClick(rowId(row))}>
                        <SvgIcon component={CrossIcon} inheritViewBox />
                    </IconButton>
                ]
            else if(isLoading) 
                return (
                    <CircularProgress size={19} />
                )
            }
        }
    ];

    const aggregatedData = useMemo(() => {
        return data.filter(o => filterByTerm(props.filterTerm, o)).map(o => {
            return { ...o, id: rowId(o), SupportsGoNow: o.Properties?.SupportsGoNow ?? false, SupportsRecyclingAirlines: o.Properties?.SupportsRecyclingAirlines ?? [], SupportsDisinsection: o.Properties?.SupportsDisinsection }
        }).sort((a,b) => (a.Code ?? '') > (b.Code ?? '') ? 1 : -1);
    }, [data, props.filterTerm]);

    const handleRowModesModelChange = (model: GridRowModesModel, details: GridCallbackDetails) => {
        setRowModesModel(model);
      };

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        if(params.reason === GridRowEditStopReasons.rowFocusOut)
            event.defaultMuiPrevented = true;
        else 
            setRowModesModel({...rowModesModel, [params.id]: {mode: GridRowModes.View}});
        
    };

    const handleSaveClick = (item : any) => () => {
        setRowModesModel({...rowModesModel, [rowId(item) ?? '']: {mode: GridRowModes.View }}); 
    };
    
    const handleCancelClick = (id : any) => () => {
        setRowModesModel({
          ...rowModesModel,
          [id]: {mode: GridRowModes.View, ignoreModifications: true},
        });
      };

    const updateItem = async (item : any) => {
            let payload : Airport = {
                Code: item.Code,
                Properties: {
                    SupportsGoNow: item?.SupportsGoNow ?? false,
                    SupportsRecyclingAirlines: item?.SupportsRecyclingAirlines ?? [],
                    SupportsDisinsection: item?.SupportsDisinsection ?? false,
                }
            }

            let update : any = payload;
            
            try {
                await put({ path: `/api/airline/airport/${payload.Code}`, apiName: 'api', options: { body: {...update}}});
                
                return true;
            } catch(e) {
                console.error(e);
                return false;
            } 
        }

    const processRowUpdate = (row: GridRowModel) => {
        const updatedRow = { ...row, isNew: false, IsLoading: true };
        
        updateItem(row as Airport & DynamoDbObject).then(success => {
            apiRef.current.updateRows([{ id: rowId(row), IsLoading: false }]);

            if(success) 
                toast.success(`Changes to airport saved successfully.`);
            else
                toast.error(`An error occurred while saving airport`);
        })
        return updatedRow;
    };

    return (

        <DataGridPremium
            apiRef={apiRef}
                            autoHeight 
                            rows={aggregatedData}
                            columns={columns}
                            editMode='row'
                            pagination
                            loading={status === Status.Fetching || status === Status.Idle || loading}
                            getRowClassName={(params) => { return params.indexRelativeToCurrentPage % 2 === 1 ? `tui-grid-alternate-row` : ''}}
                            isCellEditable={() => true }
                            rowModesModel={rowModesModel}
                            onRowModesModelChange={(m, d) => handleRowModesModelChange(m, d)}
                            onRowEditStop={handleRowEditStop}
                            processRowUpdate={processRowUpdate}
        />
    );
});

export default AirportsGrid;